import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { getGuarantor } from "../../sdk/guarantor/guarantor";
import { useFormatAmount } from "../../hooks/useFormatAmount";
import { useFormattedDate } from "../../hooks/useFormattedDate";

const Guarantor = () => {
  const { id } = useParams();
  const formatAmount = useFormatAmount();
  const formattedDate = useFormattedDate();
  const navigate = useNavigate();

  const { data: guarantor } = useQuery({
    queryKey: ["guarantor", id],
    queryFn: () => getGuarantor(id),
    initialData: {},
    enabled: !!id,
  });
  return (
    <div className="w-full pl-[10px] py-[10px]">
      <p className="text-[20px] mb-[10px] font-bold text-[#222222]">
        <span
          className="cursor-pointer"
          onClick={() => navigate("/dashboard/guarantors")}
        >
          Guarantors
        </span>{" "}
        {">"} <span>Guarantor</span>
      </p>
      <div className="bg-white rounded-xl shadow p-6 flex gap-6">
        {/* Left Side - Profile Image */}
        {guarantor.guarantorPhoto && (
          <img
            src={guarantor.guarantorPhoto}
            alt="Guarantor"
            className="w-[120px] h-[120px] object-cover rounded-full border"
          />
        )}
        {/* Right Side - Info */}
        <div className="flex-1">
          <h2 className="text-xl font-bold text-[#222]">
            {guarantor.guarantorName} ({guarantor.public_id})
          </h2>
          <p className="text-sm text-gray-600">{guarantor.residenceLocation}</p>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <p className="font-semibold text-gray-700">Phone</p>
              <p>{guarantor.guarantorPhone}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-700">Email</p>
              <p>{guarantor.guarantorEmail || "N/A"}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-700">Date Added</p>
              <p>{formattedDate(guarantor.createdAt) || "N/A"}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-700">Guarantee Amount</p>
              <p className="text-[#000000] font-bold">
                {formatAmount(guarantor.guaranteeAmount)}
              </p>
            </div>
            <div>
              <p className="font-semibold text-gray-700">Status</p>
              <span
                className={`px-3 py-1 rounded-full capitalize text-sm font-medium ${
                  guarantor.status === "pending"
                    ? "bg-yellow-100 text-yellow-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {guarantor.status}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-6">
        <div>
          <p className="font-semibold mb-2">Business Photo</p>
          {guarantor.guarantorBusinessPhoto ? (
            <img
              src={guarantor.guarantorBusinessPhoto}
              alt="Business"
              className="w-full h-[200px] object-cover rounded-lg border"
            />
          ) : (
            <div className="w-full h-[200px] flex items-center justify-center border rounded-lg text-gray-500">
              No image uploaded
            </div>
          )}
        </div>
        <div>
          <p className="font-semibold mb-2">Signature</p>
          {guarantor.signatureUrl ? (
            <img
              src={guarantor.signatureUrl}
              alt="Signature"
              className="w-full h-[200px] object-contain rounded-lg border"
            />
          ) : (
            <div className="w-full h-[200px] flex items-center justify-center border rounded-lg text-gray-500">
              No signature uploaded
            </div>
          )}
        </div>
      </div>
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-[#222] mb-4">Chattels</h3>
        {guarantor.chattels?.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">ID</th>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Location</th>
                  <th className="p-2 text-left">Value</th>
                  <th className="p-2 text-left">Condition</th>
                  <th className="p-2 text-left">Description</th>
                  <th className="p-2 text-left">Images</th>
                </tr>
              </thead>
              <tbody>
                {guarantor.chattels.map((chattel) => (
                  <tr key={chattel.id} className="border-t">
                    <td className="p-2">{chattel.public_id}</td>
                    <td className="p-2">{chattel.name}</td>
                    <td className="p-2">{chattel.location}</td>
                    <td className="p-2 font-semibold text-[#000000]">
                      {formatAmount(chattel.value)}
                    </td>
                    <td className="p-2">{chattel.condition}</td>
                    <td className="p-2">{chattel.description}</td>
                    <td className="p-2">
                      {chattel.images?.length > 0 ? (
                        <img
                          src={chattel.images[0].imageUrl}
                          alt="Chattel"
                          className="w-[60px] h-[60px] object-cover rounded-lg border"
                        />
                      ) : (
                        "No image"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">No chattels available</p>
        )}
      </div>
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-[#222] mb-4">Documents</h3>

        {guarantor.documents?.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">ID</th>
                  <th className="p-2 text-left">Document Type</th>
                  <th className="p-2 text-left">Uploaded At</th>
                  <th className="p-2 text-left">File</th>
                </tr>
              </thead>
              <tbody>
                {guarantor.documents.map((doc) => (
                  <tr key={doc.id} className="border-t">
                    <td className="p-2">{doc?.id?.slice(0, 20)}</td>
                    <td className="p-2">{doc.documentType}</td>
                    <td className="p-2">{formattedDate(doc.uploadedAt)}</td>
                    <td className="p-2">
                      <a
                        href={doc.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        View Document
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">No documents uploaded</p>
        )}
      </div>
    </div>
  );
};

export default Guarantor;
